import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiHome,
  FiFileText,
  FiLayers,
  FiTag,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiImage,
  FiUpload,
  FiTrash2,
} from "react-icons/fi";
import { useRoom } from "../../context/RoomContext";
import { validateRoomForm, getFieldImage } from "../../utils/roomHelpers";
import { roomPhotoService } from "../../services/rooms";
import "./CreateRoomModal.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.15 },
  },
};

const formItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.08, duration: 0.3 },
  }),
};

// Sub-components
const FormField = ({
  icon: Icon,
  label,
  required,
  error,
  charCount,
  minCount,
  children,
  index,
}) => (
  <motion.div
    className="crm-form-group"
    custom={index}
    variants={formItemVariants}
    initial="hidden"
    animate="visible"
  >
    <label className="crm-label">
      <Icon className="crm-label-icon" />
      <span>{label}</span>
      {required && <span className="crm-required">*</span>}
    </label>
    {children}
    <div className="crm-field-footer">
      {charCount !== undefined && (
        <span
          className={`crm-char-count ${
            charCount >= minCount ? "crm-char-valid" : ""
          }`}
        >
          {charCount}/{minCount} min
        </span>
      )}
      {error && (
        <motion.span
          className="crm-error"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FiAlertCircle />
          {error}
        </motion.span>
      )}
    </div>
  </motion.div>
);

const CreateRoomModal = ({ onClose, onSuccess }) => {
  const { createRoom, fields, fetchFields, loading } = useRoom();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    field: "",
    subField: "",
    photoURL: "",
  });

  const [errors, setErrors] = useState({});
  const [subFields, setSubFields] = useState([]);

  // Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoDragActive, setPhotoDragActive] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (Object.keys(fields).length === 0) {
      fetchFields();
    }
  }, [fields, fetchFields]);

  useEffect(() => {
    if (formData.field && fields[formData.field]) {
      setSubFields(fields[formData.field]);
      // Reset subfield if field changes
      if (
        formData.subField &&
        !fields[formData.field].includes(formData.subField)
      ) {
        setFormData((prev) => ({ ...prev, subField: "" }));
      }
    } else {
      setSubFields([]);
    }
  }, [formData.field, fields, formData.subField]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFieldChange = (e) => {
    const field = e.target.value;
    setFormData((prev) => ({
      ...prev,
      field,
      subField: "",
      photoURL: getFieldImage(field),
    }));
    if (errors.field) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.field;
        return newErrors;
      });
    }
  };

  // Photo upload handlers
  const handlePhotoSelect = useCallback((file) => {
    if (!file) return;

    // Validate file
    const validation = roomPhotoService.validateFile(file);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, photo: validation.error }));
      return;
    }

    // Clear any photo errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.photo;
      return newErrors;
    });

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setPhotoFile(file);
    setPhotoPreview(previewUrl);
  }, []);

  const handlePhotoDrop = useCallback((e) => {
    e.preventDefault();
    setPhotoDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handlePhotoSelect(e.dataTransfer.files[0]);
    }
  }, [handlePhotoSelect]);

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    setPhotoDragActive(true);
  };

  const handlePhotoDragLeave = () => {
    setPhotoDragActive(false);
  };

  const handlePhotoRemove = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = validateRoomForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      // Create the room first
      const newRoom = await createRoom(formData);
      
      // If photo was selected, upload it after room creation
      if (photoFile && newRoom?._id) {
        setPhotoUploading(true);
        try {
          await roomPhotoService.uploadRoomPhoto(newRoom._id, photoFile);
        } catch (photoErr) {
          // Room was created but photo upload failed - continue anyway
} finally {
          setPhotoUploading(false);
        }
      }

      // Clean up preview URL
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }

      if (onSuccess) {
        onSuccess(newRoom);
      }
    } catch (err) {
      setErrors({ submit: err.message || "Failed to create lab" });
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const fieldsList = Object.keys(fields);

  return (
    <AnimatePresence>
      <motion.div
        className="crm-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleOverlayClick}
      >
        <motion.div
          className="crm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="crm-header">
            <div className="crm-header-content">
              <div className="crm-header-icon">
                <FiHome />
              </div>
              <div className="crm-header-text">
                <h2 className="crm-title">Create Research Lab</h2>
                <p className="crm-subtitle">Set up a new collaborative space</p>
              </div>
            </div>
            <motion.button
              className="crm-close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          </div>

          {/* Body */}
          <div className="crm-body">
            <form onSubmit={handleSubmit} className="crm-form">
              {/* Room Name */}
              <FormField
                icon={FiHome}
                label="Lab Name"
                required
                error={errors.name}
                charCount={formData.name.length}
                minCount={15}
                index={0}
              >
                <input
                  type="text"
                  name="name"
                  className={`crm-input ${
                    errors.name ? "crm-input-error" : ""
                  }`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter a descriptive lab name..."
                  disabled={loading}
                />
              </FormField>

              {/* Description */}
              <FormField
                icon={FiFileText}
                label="Description"
                required
                error={errors.description}
                charCount={formData.description.length}
                minCount={30}
                index={1}
              >
                <textarea
                  name="description"
                  className={`crm-textarea ${
                    errors.description ? "crm-input-error" : ""
                  }`}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your research lab's purpose and goals..."
                  rows={4}
                  disabled={loading}
                />
              </FormField>

              {/* Field Selection */}
              <FormField
                icon={FiLayers}
                label="Research Field"
                required
                error={errors.field}
                index={2}
              >
                <select
                  name="field"
                  className={`crm-select ${
                    errors.field ? "crm-input-error" : ""
                  }`}
                  value={formData.field}
                  onChange={handleFieldChange}
                  disabled={loading}
                >
                  <option value="">Select a research field...</option>
                  {fieldsList.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* Subfield Selection */}
              <AnimatePresence>
                {formData.field && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FormField
                      icon={FiTag}
                      label="Subfield"
                      required
                      error={errors.subField}
                      index={3}
                    >
                      <select
                        name="subField"
                        className={`crm-select ${
                          errors.subField ? "crm-input-error" : ""
                        }`}
                        value={formData.subField}
                        onChange={handleChange}
                        disabled={loading}
                      >
                        <option value="">Select a subfield...</option>
                        {subFields.map((subField) => (
                          <option key={subField} value={subField}>
                            {subField}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Room Photo (Optional) */}
              <motion.div
                className="crm-form-group"
                custom={4}
                variants={formItemVariants}
                initial="hidden"
                animate="visible"
              >
                <label className="crm-label">
                  <FiImage className="crm-label-icon" />
                  <span>Lab Photo</span>
                  <span className="crm-optional">(optional)</span>
                </label>
                
                <div
                  className={`crm-photo-upload ${photoDragActive ? "crm-photo-upload--active" : ""} ${photoPreview ? "crm-photo-upload--has-preview" : ""}`}
                  onDrop={handlePhotoDrop}
                  onDragOver={handlePhotoDragOver}
                  onDragLeave={handlePhotoDragLeave}
                  onClick={() => !photoPreview && photoInputRef.current?.click()}
                >
                  {photoPreview ? (
                    <div className="crm-photo-preview">
                      <img src={photoPreview} alt="Preview" />
                      <motion.button
                        type="button"
                        className="crm-photo-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePhotoRemove();
                        }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiTrash2 />
                      </motion.button>
                    </div>
                  ) : (
                    <div className="crm-photo-placeholder">
                      <FiUpload className="crm-photo-icon" />
                      <span className="crm-photo-text">
                        Drag & drop an image or click to browse
                      </span>
                      <span className="crm-photo-hint">
                        Max 5MB • JPG, PNG, WebP, GIF
                      </span>
                    </div>
                  )}
                  
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    className="crm-photo-input"
                    onChange={(e) => e.target.files?.[0] && handlePhotoSelect(e.target.files[0])}
                  />
                </div>
                
                {errors.photo && (
                  <motion.span
                    className="crm-error"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <FiAlertCircle />
                    {errors.photo}
                  </motion.span>
                )}
                
                {!photoPreview && (
                  <div className="crm-photo-fallback-note">
                    <span>If no photo is uploaded, a default image based on your field selection will be used.</span>
                  </div>
                )}
              </motion.div>

              {/* Submit Error */}
              <AnimatePresence>
                {errors.submit && (
                  <motion.div
                    className="crm-submit-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <FiAlertCircle />
                    <span>{errors.submit}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="crm-actions">
                <motion.button
                  type="button"
                  className="crm-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className="crm-btn-primary"
                  disabled={loading || photoUploading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading || photoUploading ? (
                    <>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        style={{ display: "inline-flex" }}
                      >
                        <FiLoader />
                      </motion.span>
                      <span>{photoUploading ? "Uploading photo..." : "Creating..."}</span>
                    </>
                  ) : (
                    <>
                      <FiCheck />
                      <span>Create Lab</span>
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateRoomModal;
